import React from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';
import { EnhancedButton } from './EnhancedButton.jsx';

const ProgressiveFormStep = ({
  stepNumber,
  totalSteps,
  title,
  description,
  children,
  isActive = false,
  isCompleted = false,
  canNavigate = false,
  onStepClick,
  className,
  ...props
}) => {
  const handleStepClick = () => {
    if (canNavigate && onStepClick) {
      onStepClick(stepNumber);
    }
  };

  return (
    <div className={cn("relative", className)} {...props}>
      {/* Step Header */}
      <div 
        className={cn(
          "flex items-center space-x-4 mb-6 cursor-pointer transition-all duration-200",
          canNavigate && "hover:bg-muted/30 rounded-lg p-3 -m-3",
          !canNavigate && "cursor-default"
        )}
        onClick={handleStepClick}
      >
        {/* Step Number */}
        <div 
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300",
            isCompleted && "bg-success text-success-foreground",
            isActive && !isCompleted && "bg-primary text-primary-foreground ring-4 ring-primary/20",
            !isActive && !isCompleted && "bg-muted text-muted-foreground"
          )}
        >
          {isCompleted ? (
            <Icon name="Check" size={16} />
          ) : (
            stepNumber
          )}
        </div>

        {/* Step Info */}
        <div className="flex-1">
          <h3 className={cn(
            "font-semibold transition-colors duration-200",
            isActive ? "text-primary" : isCompleted ? "text-success" : "text-muted-foreground"
          )}>
            {title}
          </h3>
          <p className={cn(
            "text-sm transition-colors duration-200",
            isActive ? "text-foreground" : "text-muted-foreground"
          )}>
            {description}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="text-xs text-muted-foreground">
          {stepNumber} of {totalSteps}
        </div>
      </div>

      {/* Step Content */}
      <div 
        className={cn(
          "transition-all duration-300 overflow-hidden",
          isActive ? "max-h-none opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="ml-14 space-y-6">
          {children}
        </div>
      </div>

      {/* Connector Line */}
      {stepNumber < totalSteps && (
        <div 
          className={cn(
            "absolute left-5 top-16 w-px h-8 transition-colors duration-300",
            isCompleted ? "bg-success" : "bg-border"
          )}
        />
      )}
    </div>
  );
};

export const ProgressiveFormNavigation = ({
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  nextLabel = "Next",
  previousLabel = "Previous",
  skipLabel = "Skip",
  showSkip = false,
  isNextDisabled = false,
  isPreviousDisabled = false,
  isLoading = false,
  className
}) => {
  return (
    <div className={cn("flex items-center justify-between pt-6 border-t border-border", className)}>
      <div>
        {currentStep > 1 && (
          <EnhancedButton
            variant="ghost"
            size="md"
            onClick={onPrevious}
            disabled={isPreviousDisabled || isLoading}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            {previousLabel}
          </EnhancedButton>
        )}
      </div>

      <div className="flex items-center space-x-3">
        {showSkip && currentStep < totalSteps && (
          <EnhancedButton
            variant="ghost"
            size="md"
            onClick={onSkip}
            disabled={isLoading}
            className="text-muted-foreground hover:text-foreground"
          >
            {skipLabel}
          </EnhancedButton>
        )}

        <EnhancedButton
          variant="primary"
          size="md"
          onClick={onNext}
          disabled={isNextDisabled}
          loading={isLoading}
          iconName={currentStep === totalSteps ? "Calculator" : "ChevronRight"}
          iconPosition="right"
          className="min-w-[120px]"
        >
          {currentStep === totalSteps ? "Calculate ROI" : nextLabel}
        </EnhancedButton>
      </div>
    </div>
  );
};

export const ProgressiveFormProgress = ({
  currentStep,
  totalSteps,
  className
}) => {
  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <div className={cn("mb-8", className)}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="text-sm text-muted-foreground">
          {Math.round(progressPercentage)}% Complete
        </span>
      </div>
      
      <div className="w-full bg-muted rounded-full h-2">
        <div 
          className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  );
};

export default ProgressiveFormStep;
